import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { formatElapsed } from "@/lib/elapsed";
import { isSessionLate, minutesLeft, minutesOver } from "@/lib/multi-view";
import type { CookingSession } from "@/types/cooking-session";

interface SessionTimerProps {
  session: CookingSession;
  seconds: number;
  size?: "lg" | "md";
  className?: string;
}

/**
 * "15:58 of 45m" with "29m left" under it, or "Late - +20m over" in red once
 * the target has passed. Takes its seconds from the screen's one clock.
 */
export function SessionTimer({ session, seconds, size = "md", className }: SessionTimerProps) {
  const { t } = useLingui();
  const late = isSessionLate(session, seconds);
  const over = minutesOver(session, seconds);
  const left = minutesLeft(session, seconds);

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-baseline gap-2">
        <span
          data-testid="session-timer"
          className={cn(
            "font-semibold tabular-nums tracking-tight",
            size === "lg" ? "text-5xl" : "text-3xl",
            late ? "text-(--color-danger-text)" : "text-(--color-text-primary)",
          )}
        >
          {formatElapsed(seconds)}
        </span>
        <span className="text-sm text-(--color-text-secondary)">{t`of ${session.targetMinutes}m`}</span>
      </div>
      <span className={cn("text-sm font-medium", late ? "text-(--color-danger-text)" : "text-(--color-text-secondary)")}>
        {late ? t`Late · +${over}m over` : t`${left}m left`}
      </span>
    </div>
  );
}
